export const SELECT_OTHER_VALUE = "__other__";
export const SELECT_OTHER_LABEL = "Other";
export const SELECT_OTHER_PREFIX = "Other:";
export const SELECT_OTHER_TEXT_KEY = "__other_text__";

type SelectQuestionType = "single-select" | "multi-select";
type OtherOptionQuestionType = SelectQuestionType | "ranking";

interface SelectQuestionLike {
  questionType?: unknown;
  options?: Record<string, unknown> | null;
}

export function normalizeSelectQuestionType(
  questionType: unknown,
): SelectQuestionType | null {
  const type = normalizeOtherOptionQuestionType(questionType);
  return type === "ranking" ? null : type;
}

export function normalizeOtherOptionQuestionType(
  questionType: unknown,
): OtherOptionQuestionType | null {
  const key = String(questionType ?? "").toLowerCase();
  if (
    key === "single-select" ||
    key === "single_select" ||
    key === "single select"
  ) {
    return "single-select";
  }
  if (
    key === "multi-select" ||
    key === "multi_select" ||
    key === "multi select"
  ) {
    return "multi-select";
  }
  if (key === "ranking") return "ranking";
  return null;
}

export function isSelectOtherEnabled(question: SelectQuestionLike): boolean {
  const type = normalizeOtherOptionQuestionType(question.questionType);
  if (!type) return false;

  const explicit = question.options?.allowOther;
  if (typeof explicit === "boolean") return explicit;

  return type === "single-select";
}

export function toOtherAnswer(
  value: string,
  options: { allowBlank?: boolean } = {},
) {
  const trimmed = value.trim();
  if (!trimmed && !options.allowBlank) return "";
  return trimmed ? `${SELECT_OTHER_PREFIX} ${trimmed}` : SELECT_OTHER_PREFIX;
}

export function isOtherAnswer(value: unknown): boolean {
  const trimmed = String(value ?? "").trim();
  return (
    trimmed === SELECT_OTHER_VALUE ||
    trimmed === SELECT_OTHER_PREFIX ||
    trimmed.startsWith(`${SELECT_OTHER_PREFIX} `)
  );
}

export function isBlankOtherAnswer(value: unknown): boolean {
  return isOtherAnswer(value) && otherValueFromAnswer(value).length === 0;
}

export function otherValueFromAnswer(value: unknown): string {
  const trimmed = String(value ?? "").trim();
  if (trimmed === SELECT_OTHER_VALUE) return "";
  if (
    !trimmed.startsWith(`${SELECT_OTHER_PREFIX} `) &&
    trimmed !== SELECT_OTHER_PREFIX
  ) {
    return "";
  }
  return trimmed.slice(SELECT_OTHER_PREFIX.length).trim();
}

export function hasOtherAnswerText(value: unknown): boolean {
  const trimmed = String(value ?? "").trim();
  if (
    !trimmed ||
    trimmed === SELECT_OTHER_VALUE ||
    trimmed === SELECT_OTHER_LABEL ||
    trimmed === SELECT_OTHER_PREFIX
  ) {
    return false;
  }
  if (isOtherAnswer(trimmed)) return otherValueFromAnswer(trimmed).length > 0;
  return true;
}

export function encodeOtherTextValue(value: string): string {
  return encodeURIComponent(value);
}

export function decodeOtherTextValue(value: unknown): string {
  const raw = String(value ?? "");
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
